import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Eye, EyeOff, AlertCircle, Loader2, Lock, CheckCircle,
  ShieldCheck, Mail, Smartphone, RefreshCw, ArrowLeft,
} from "lucide-react";
import { trpc } from "@/utils/trpc";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LanguageToggle } from "@/components/LanguageSelector";

// ─── Types ────────────────────────────────────────────────────────────────────
type Step = "credentials" | "otp" | "success";

// ─── OTP Input — 6 individual boxes ──────────────────────────────────────────
function OtpInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // Always produce exactly 4 slots; fill missing positions with empty string
  const raw = value.replace(/\D/g, "").slice(0, 4);
  const digits = Array.from({ length: 4 }, (_, i) => raw[i] ?? "");

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const next = digits.slice();
      if (next[i]) {
        next[i] = "";
        onChange(next.join(""));
      } else if (i > 0) {
        next[i - 1] = "";
        onChange(next.join(""));
        inputs.current[i - 1]?.focus();
      }
      e.preventDefault();
    }
  };

  const handleChange = (i: number, raw: string) => {
    const char = raw.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const next = digits.slice();
    next[i] = char;
    onChange(next.join(""));
    if (i < 3) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted) {
      onChange(pasted.slice(0, 4));
      inputs.current[Math.min(pasted.length, 3)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onFocus={(e) => e.target.select()}
          style={{
            width: "48px",
            height: "56px",
            textAlign: "center",
            fontSize: "22px",
            fontWeight: 700,
            borderRadius: "10px",
            border: d ? "2px solid #5B2C93" : "2px solid #D1D5DB",
            background: d ? "#F0EBF8" : "#FFFFFF",
            color: "#2C2C2C",
            outline: "none",
            boxSizing: "border-box",
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
      ))}
    </div>
  );
}

// ─── Countdown timer ──────────────────────────────────────────────────────────
function useCountdown(seconds: number) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    setRemaining(seconds);
    if (seconds <= 0) return;
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [seconds]);
  return remaining;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Login() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  // Step state
  const [step, setStep] = useState<Step>("credentials");

  // Credentials step
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OTP step
  const [otpCode, setOtpCode] = useState("");
  const [otpInfo, setOtpInfo] = useState<{
    channel: "email" | "sms" | "both";
    maskedEmail?: string;
    maskedPhone?: string;
    expiresAt: Date;
  } | null>(null);
  const [resendCountdown, setResendCountdown] = useState(60);
  const countdown = useCountdown(resendCountdown);

  // Shared
  const [error, setError] = useState<{ code?: string; message: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const sendOtpMutation = trpc.auth.sendOtp.useMutation({
    onSuccess: (data) => {
      setOtpInfo({
        channel: data.channel,
        maskedEmail: data.maskedEmail,
        maskedPhone: data.maskedPhone,
        expiresAt: new Date(data.expiresAt),
      });
      setResendCountdown(60);
      setOtpCode("");
      setError(null);
      setStep("otp");
    },
    onError: (err: any) => {
      const code = typeof err?.message === "string" ? err.message : undefined;
      if (code === "USER_NOT_FOUND") return setError({ code, message: "No account found with that email." });
      if (code === "INVALID_PASSWORD") return setError({ code, message: "Incorrect password." });
      if (code === "PASSWORD_NOT_SET") return setError({ code, message: "Password not set. Please contact an administrator." });
      if (code === "OTP_SEND_FAILED") return setError({ message: "Failed to send verification code. Please try again." });
      setError({ message: err?.message || "Login failed. Please try again." });
    },
  });

  const verifyOtpMutation = trpc.auth.verifyOtp.useMutation({
    onSuccess: (data) => {
      if (data.sessionToken) {
        localStorage.setItem("app_session_token", data.sessionToken);
      }
      setStep("success");
      setTimeout(() => { window.location.href = "/"; }, 800);
    },
    onError: (err: any) => {
      const code = typeof err?.message === "string" ? err.message : undefined;
      if (code === "OTP_INVALID") return setError({ code, message: "Invalid or expired code. Please try again." });
      if (code === "OTP_MAX_ATTEMPTS") return setError({ code, message: "Too many attempts. Please request a new code." });
      setError({ message: err?.message || "Verification failed." });
    },
  });

  const requestResetMutation = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      setSuccessMessage("Password reset email sent! Please check your inbox.");
      setIsSendingReset(false);
    },
    onError: () => {
      setError({ message: "Failed to send reset email. Please try again." });
      setIsSendingReset(false);
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCredentialsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) return setError({ message: t("validation.required") });
    sendOtpMutation.mutate({ email, password, channel: "both" });
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (otpCode.replace(/\D/g, "").length < 4) return setError({ message: "Please enter your verification code." });
    verifyOtpMutation.mutate({ email, code: otpCode.replace(/\D/g, ""), rememberMe });
  };

  const handleResend = () => {
    setError(null);
    setOtpCode("");
    sendOtpMutation.mutate({ email, password, channel: "both" });
  };

  // ── Channel description ───────────────────────────────────────────────────────
  const channelDescription = () => {
    if (!otpInfo) return "";
    if (otpInfo.channel === "both") {
      return `We sent a 4-digit code to ${otpInfo.maskedEmail ?? "your email"} and ${otpInfo.maskedPhone ?? "your phone"}.`;
    }
    if (otpInfo.channel === "sms") {
      return `We sent a 4-digit code to ${otpInfo.maskedPhone ?? "your phone"}.`;
    }
    return `We sent a 4-digit code to ${otpInfo.maskedEmail ?? "your email"}.`;
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen w-full flex" dir={isRTL ? "rtl" : "ltr"}>
      {/* Left Side — Purple brand panel */}
      <div className={`hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#5B2C93] via-[#5B2C93] to-[#5B2C93] relative overflow-hidden ${isRTL ? "order-2" : "order-1"}`}>
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-16">
          <div className="mb-8">
            <img src="/center3-logo-transparent.png" alt="permit3" className="h-[100px] max-w-full object-contain" />
          </div>
          <h2 className="text-white text-xl font-medium text-center mb-2">{t("auth.tagline")}</h2>
          <h3 className="text-white/90 text-lg text-center mb-16">{t("auth.taglineSecondary")}</h3>
          <div className="absolute bottom-12 left-0 right-0 text-center">
            <p className="text-white/80 text-sm tracking-widest">{t("auth.footerTagline")}</p>
          </div>
        </div>
      </div>

      {/* Right Side — Form */}
      <div className={`w-full lg:w-1/2 flex items-center justify-center bg-[#F5F5F5] ${isRTL ? "order-1" : "order-2"}`}>
        <div className="w-full max-w-md px-8">
          <div className="bg-white rounded-2xl shadow-lg p-10">

            {/* ── Step 1: Credentials ── */}
            {step === "credentials" && (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-medium text-[#2C2C2C] mb-2">{t("auth.welcomeBackTitle")}</h1>
                  <p className="text-[#6B6B6B] text-sm">{t("auth.signInSubtitle")}</p>
                </div>

                {error && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                )}
                {successMessage && (
                  <Alert className="mb-6 bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">{successMessage}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleCredentialsSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#2C2C2C]">{t("auth.email")}</label>
                    <Input
                      type="email"
                      placeholder={t("auth.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#2C2C2C]">{t("auth.password")}</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("auth.passwordPlaceholder")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 pl-4 pr-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B6B6B] ${isRTL ? "left-4" : "right-4"}`}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={(c) => setRememberMe(c as boolean)}
                    />
                    <label htmlFor="remember" className="text-sm text-[#6B6B6B] cursor-pointer">
                      {t("auth.rememberDevice")}
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={sendOtpMutation.isPending}
                    className="w-full h-12 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white font-medium rounded-lg text-base"
                  >
                    {sendOtpMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      t("auth.signIn")
                    )}
                  </Button>
                </form>

                <div className="mt-4 space-y-3 border-t pt-4">
                  <div className="text-center">
                    <p className="text-xs text-[#6B6B6B] mb-2">Having trouble signing in?</p>
                    <button
                      type="button"
                      disabled={isSendingReset || !email}
                      onClick={() => {
                        setIsSendingReset(true);
                        setError(null);
                        setSuccessMessage(null);
                        requestResetMutation.mutate({ email });
                      }}
                      className="text-sm font-medium text-[#5B2C93] hover:text-[#3D1C5E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSendingReset ? t("auth.sendingReset") : "Reset Password"}
                    </button>
                  </div>

                  {(error?.code === "INVALID_PASSWORD" || error?.code === "PASSWORD_NOT_SET") && email && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <p className="text-xs text-blue-700 mb-2">Password not set or incorrect?</p>
                      <button
                        type="button"
                        disabled={isSendingReset}
                        onClick={() => {
                          setIsSendingReset(true);
                          setError(null);
                          setSuccessMessage(null);
                          requestResetMutation.mutate({ email });
                        }}
                        className="text-sm font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                      >
                        {isSendingReset ? t("auth.sendingReset") : "Send Reset Email"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Step 2: OTP Entry ── */}
            {step === "otp" && (
              <>
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0EBF8] flex items-center justify-center mx-auto mb-4">
                    <ShieldCheck className="h-7 w-7 text-[#5B2C93]" />
                  </div>
                  <h1 className="text-2xl font-medium text-[#2C2C2C] mb-2">Verify Your Identity</h1>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">{channelDescription()}</p>
                </div>

                {/* Channel badges */}
                {otpInfo && (
                  <div className="flex items-center justify-center gap-3 mb-6">
                    {(otpInfo.channel === "email" || otpInfo.channel === "both") && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        <Mail className="h-3.5 w-3.5" />
                        Email sent
                      </span>
                    )}
                    {(otpInfo.channel === "sms" || otpInfo.channel === "both") && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                        <Smartphone className="h-3.5 w-3.5" />
                        SMS sent
                      </span>
                    )}
                  </div>
                )}

                {error && (
                  <Alert variant="destructive" className="mb-5">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-[#2C2C2C] text-center block">
                      Enter 4-digit verification code
                    </label>
                    <OtpInput
                      value={otpCode}
                      onChange={setOtpCode}
                      disabled={verifyOtpMutation.isPending}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={verifyOtpMutation.isPending || otpCode.replace(/\D/g, "").length < 4}
                    className="w-full h-12 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white font-medium rounded-lg text-base"
                  >
                    {verifyOtpMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Verify & Sign In"
                    )}
                  </Button>
                </form>

                {/* Resend + back */}
                <div className="mt-5 space-y-3">
                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-sm text-[#9CA3AF]">
                        Resend code in <span className="font-medium text-[#5B2C93]">{countdown}s</span>
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={sendOtpMutation.isPending}
                        className="inline-flex items-center gap-1.5 text-sm text-[#5B2C93] hover:underline disabled:opacity-50 font-medium"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Resend code
                      </button>
                    )}
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setStep("credentials"); setError(null); setOtpCode(""); }}
                      className="inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-[#2C2C2C]"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Back to sign in
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── Step 3: Success ── */}
            {step === "success" && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-medium text-[#2C2C2C] mb-2">Verified!</h2>
                <p className="text-[#6B6B6B] text-sm">Redirecting you to the dashboard…</p>
                <Loader2 className="h-5 w-5 animate-spin text-[#5B2C93] mx-auto mt-4" />
              </div>
            )}

            {/* Language toggle (always visible) */}
            {step !== "success" && (
              <div className="mt-6 flex justify-center">
                <LanguageToggle />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-[#6B6B6B]">
              <span>{t("auth.copyright")}</span>
              <span>•</span>
              <a href="#" className="hover:text-[#5B2C93]">{t("auth.privacyPolicy")}</a>
              <span>•</span>
              <a href="#" className="hover:text-[#5B2C93]">{t("auth.termsOfService")}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
